import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet } from 'react-native'
import { object, string } from 'yup'
import { Draft, Address } from 'zens-store'
import { AddressForm, Form, FormCheckbox, FormProps, NavigationConfirm } from '@dms/component'
import { observer } from 'mobx-react'

const styles = StyleSheet.create({
  checkbox: {
    minHeight: 46,
  },
})

export interface Data {
  id: number
  name?: string
  location_enabled?: boolean
  location?: {
    name?: string
  }
  address: Address
}

interface Props extends Omit<FormProps, 'draft' | 'config' | 'schema' | 'buttons'> {
  onSave?: (draft: Draft<Data>) => Promise<void>
}

const CompanyAddForm = (props: Props) => {
  const { onSave, ...otherProps } = props
  const draft = useRef(new Draft<Data>({ id: NaN, location_enabled: true, address: {} as Address })).current

  const locationEnabled = draft.data['location_enabled']
  const { t } = useTranslation()

  const config = {
    groups: [
      {
        label: t('General details'),
        fields: [
          {
            key: 'name',
            label: t('Company name'),
            help: t('A company is used in this application as a container for multiple locations.'),
            fieldProps: {
              testID: 'CompanyAdd.Company',
            },
          },
          {
            key: 'location_enabled',
            FieldInputComponent: FormCheckbox,
            fieldProps: {
              label: t('I would like to add a first location for this company'),
              testID: 'CompanyAdd.LocationEnabled',
            },
            style: styles.checkbox,
          },
        ],
      },
      ...(locationEnabled
        ? [
            {
              label: t('Location'),
              fields: [
                {
                  key: 'location.name',
                  label: t('Name of location'),
                  fieldProps: {
                    testID: 'CompanyAdd.Location',
                  },
                },
              ],
            },
          ]
        : []),
    ],
  }

  const schema = object({
    name: string().required(t('Company name is required')),
    ...(locationEnabled
      ? {
          location: object({
            name: string().required(t('Location name is required')),
          }),
        }
      : {}),
  })

  const buttons = [
    [
      {
        title: t('Create company'),
        onPress: onSave,
        validate: true,
      },
      {
        title: t('Cancel'),
        href: `/companies`,
        light: true,
      },
    ],
  ]

  return (
    <>
      {locationEnabled ? (
        <AddressForm draft={draft} config={config} schema={schema} buttons={buttons} {...otherProps} />
      ) : (
        <Form draft={draft} config={config} schema={schema} buttons={buttons} {...otherProps} />
      )}
      <NavigationConfirm draft={draft} />
    </>
  )
}

export default observer(CompanyAddForm)
